/**
 * Sales "Today" — the Deal Coach cards, pipeline numbers and next meetings.
 * Mirrors apps/api/src/domain/sales/today.ts (GET /v1/sales/today).
 */

import { api } from './api';

export type SignalReason = 'declined' | 'parked' | 'said_yes' | 'prep' | 'awaiting_reply' | 'proposal_waiting' | 'owed' | 'gone_quiet' | 'no_next_step' | 'concern';
export type SignalSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface SignalEvidence {
  kind: 'stage' | 'value' | 'touch' | 'next' | 'commitment' | 'quote';
  text: string;
  at?: string | null;
}

export interface DealSignal {
  key: string;
  dealId: string;
  dealName: string;
  company: string | null;
  contactEmail: string | null;
  provider: string;
  valueCents: number;
  stage: string;
  daysInStage: number | null;
  /** Funnel position 0..1 when the CRM's stage order is known. */
  stageRank: number | null;
  reason: SignalReason;
  severity: SignalSeverity;
  headline: string;
  detail: string;
  action: { kind: 'prep' | 'call' | 'reply' | 'book' | 'deliver' | 'email' | 'close'; label: string };
  evidence: SignalEvidence[];
  lastTouch: { at: string; kind: 'call' | 'meeting' | 'email' | 'message' | 'activity'; label: string; fromUs?: boolean | null } | null;
  daysSinceTouch: number | null;
  nextMeetingAt: string | null;
  fingerprint: string;
}

export interface HygieneItem {
  key: 'dead_stage_open' | 'stale_early' | 'unpriced_engaged' | 'no_close_dates' | 'no_conversation';
  title: string;
  detail: string;
  count: number;
  valueCents: number;
  examples: Array<{ dealId: string; name: string; stage: string }>;
}

export interface PipelineStats {
  openDeals: number;
  liveDeals: number;
  deadStageDeals: number;
  pricedDeals: number;
  openValueCents: number;
  byStage: Array<{ stage: string; count: number; valueCents: number }>;
  addedLast7d: { count: number; valueCents: number };
  wonThisMonth: { count: number; valueCents: number };
  winRate90d: { won: number; lost: number; rate: number | null };
}

export interface UpcomingMeeting {
  at: string;
  title: string;
  dealId: string | null;
  dealName: string | null;
  stage: string | null;
  valueCents: number | null;
  attendees: string[];
}

export interface RecentCall {
  id: string;
  at: string;
  title: string;
  durationMin: number | null;
  dealId: string | null;
  dealName: string | null;
  stage: string | null;
  valueCents: number | null;
  prospects: string[];
  /** Open commitments our side made on this call. */
  oursOpen: number;
  /** Nothing recorded with the deal since the call and nothing booked. */
  followUpDue: boolean;
}

export interface SalesToday {
  generatedAt: string;
  crmConnected: boolean;
  coverage: {
    crmProviders: string[];
    callRecorder: boolean;
    calendar: boolean;
    email: 'connected' | 'calendar_only' | 'not_connected';
    crmMessages: 'connected' | 'missing_scope' | 'not_synced' | 'not_applicable';
    gaps: string[];
  };
  signals: DealSignal[];
  hygiene: HygieneItem[];
  hiddenCount: number;
  summary: {
    total: number;
    bySeverity: Record<SignalSeverity, number>;
    byReason: Partial<Record<SignalReason, number>>;
    valueCents: number;
  };
  pipeline: PipelineStats;
  /** The Evidence forecast's totals over the same book. */
  evidence: EvidenceTotals;
  upcoming: UpcomingMeeting[];
  recentCalls: RecentCall[];
  truncated: boolean;
}

export type EvidenceBucket = 'said_no' | 'gone_dark' | 'dead_stage' | 'at_risk' | 'live' | 'said_yes' | 'cant_judge';

/** Mirrors RealityTotals in apps/api/src/domain/sales/reality-forecast.ts. */
export interface EvidenceTotals {
  rulesVersion: number;
  crmCents: number;
  deadStageDeals: number;
  evidenceCents: number;
  unverifiedCents: number;
  atRiskCents: number;
  removedCents: number;
  removedDeals: number;
  unpricedDeals: number;
  byBucket: Record<EvidenceBucket, { deals: number; cents: number }>;
  unverifiedOwners: string[];
}

function timeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export const SALES_TODAY_KEY = ['sales-today'] as const;

export function getSalesToday(): Promise<SalesToday> {
  return api<SalesToday>(`/v1/sales/today?tz=${encodeURIComponent(timeZone())}`);
}

export type SignalAction =
  | { action: 'done'; key: string; fingerprint: string }
  | { action: 'snooze'; key: string; days: number }
  | { action: 'clear'; key: string };

export function setSignalState(body: SignalAction): Promise<{ ok: boolean }> {
  return api('/v1/sales/signals/state', { method: 'POST', body: JSON.stringify(body) });
}

/** Recount the summary for a card list (optimistic updates after done/snooze). */
export function summarize(signals: DealSignal[]): SalesToday['summary'] {
  const bySeverity: Record<SignalSeverity, number> = { critical: 0, high: 0, medium: 0, low: 0 };
  const byReason: Partial<Record<SignalReason, number>> = {};
  let valueCents = 0;
  for (const s of signals) {
    bySeverity[s.severity] += 1;
    byReason[s.reason] = (byReason[s.reason] ?? 0) + 1;
    valueCents += s.valueCents;
  }
  return { total: signals.length, bySeverity, byReason, valueCents };
}

export const REASON_LABEL: Record<SignalReason, string> = {
  declined: 'Said no',
  parked: 'On hold',
  said_yes: 'Said yes',
  prep: 'Prep',
  awaiting_reply: 'Awaiting your reply',
  proposal_waiting: 'Proposal quiet',
  owed: 'You owe them',
  gone_quiet: 'Gone quiet',
  no_next_step: 'No next step',
  concern: 'Open concern',
};

export function fmtMoney(cents: number): string {
  const d = cents / 100;
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(d >= 10_000_000 ? 0 : 1)}M`;
  if (d >= 1_000) return `$${Math.round(d / 1_000)}k`;
  return `$${Math.round(d)}`;
}
